/**
 * Pagina pubblica di scaricamento: è quello che vede il cliente quando riceve
 * un file pesante. Nessun account, nessuna registrazione, il logo di chi lo ha
 * mandato. Deve funzionare al primo colpo anche a chi non ha mai sentito
 * nominare Blagomail.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Download, Lock, Loader2, FileWarning, FileDown } from 'lucide-react';
import { filesApi } from '@/lib/api/endpoints';
import { API_URL } from '@/lib/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

function human(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function FileDownloadPage() {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const q = useQuery({
    queryKey: ['public-file', token],
    queryFn: () => filesApi.publicInfo(token!),
    enabled: !!token,
    retry: false,
  });

  const info = q.data;
  const brandColor = info?.brand.color || '#6366f1';

  async function scarica() {
    if (!token) return;
    setBusy(true); setError(null);
    try {
      /**
       * Due passi: la password si verifica in una POST (così non finisce
       * nell'indirizzo), poi si lascia scaricare il browser da solo.
       *
       * Prima ci tiravamo dentro l'intero file per poi offrirlo: con un file
       * da un gigabyte è la memoria di chi lo riceve a pagarla, non c'è
       * avanzamento, e se cade la linea si ricomincia da capo. Un link normale
       * fa tutte e tre le cose meglio.
       */
      const res = await fetch(`${API_URL}/public/files/${token}/ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: password || undefined }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { message?: string; error?: string };
        throw new Error(body.message || body.error || 'Scaricamento non riuscito');
      }
      const { ticket } = await res.json() as { ticket: string };
      window.location.href = `${API_URL}/public/files/${token}/download?t=${encodeURIComponent(ticket)}`;
      setDone(true);
    } catch (e) {
      /**
       * Questa pagina la vede il destinatario, che spesso è il cliente del
       * nostro cliente: «Failed to fetch» è l'eccezione grezza del browser, in
       * inglese, e non gli dice né cosa è successo né cosa fare. Un guasto di
       * rete si riconosce dal tipo dell'errore, non dal testo.
       */
      const rete = e instanceof TypeError
        || (e instanceof Error && /failed to fetch|load failed|network/i.test(e.message));
      setError(
        rete
          ? 'Il collegamento si è interrotto durante lo scaricamento. Riprova: il file è ancora qui.'
          : e instanceof Error ? e.message : 'Scaricamento non riuscito'
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-10">
      <div className="w-full max-w-md">
        {/* intestazione di chi manda */}
        <div className="text-center mb-6">
          {info?.brand.logoUrl ? (
            <img src={info.brand.logoUrl} alt="" className="h-12 mx-auto object-contain" />
          ) : info?.brand.name ? (
            <div className="text-lg font-bold" style={{ color: brandColor }}>{info.brand.name}</div>
          ) : null}
        </div>

        <div className="bg-background rounded-2xl border shadow-sm p-7">
          {q.isLoading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Un attimo…
            </div>
          ) : q.isError || !info ? (
            <div className="text-center py-6">
              <FileWarning className="h-9 w-9 mx-auto text-muted-foreground/50" />
              <h1 className="mt-3 font-semibold">Link non valido</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Il link potrebbe essere stato copiato male. Richiedilo a chi te l'ha mandato.
              </p>
            </div>
          ) : info.unavailable ? (
            <div className="text-center py-6">
              <FileWarning className="h-9 w-9 mx-auto text-amber-500" />
              <h1 className="mt-3 font-semibold">Non più disponibile</h1>
              <p className="mt-1 text-sm text-muted-foreground">{info.unavailable}</p>
              <p className="mt-3 text-sm text-muted-foreground">
                Scrivi a chi te l'ha inviato: può rigenerarlo in un momento.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <div
                  className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 text-white"
                  style={{ backgroundColor: brandColor }}
                >
                  <FileDown className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <h1 className="font-semibold truncate">{info.filename}</h1>
                  <p className="text-sm text-muted-foreground">
                    {human(info.sizeBytes)} · disponibile fino al{' '}
                    {format(new Date(info.expiresAt), 'd MMMM yyyy', { locale: it })}
                  </p>
                </div>
              </div>

              {info.needsPassword && (
                <div className="mt-5">
                  <label className="text-sm font-medium flex items-center gap-1.5 mb-1.5">
                    <Lock className="h-3.5 w-3.5" /> Password
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') void scarica(); }}
                    placeholder="Quella che ti è stata comunicata"
                    autoFocus
                  />
                </div>
              )}

              {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}

              <Button
                className="w-full mt-5"
                size="lg"
                style={{ backgroundColor: brandColor }}
                disabled={busy || (info.needsPassword && !password)}
                onClick={() => void scarica()}
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                {busy ? 'Scarico…' : 'Scarica il file'}
              </Button>

              {done && (
                <p className="mt-3 text-sm text-emerald-600 text-center">
                  Fatto. Se non lo trovi, guarda nella cartella dei download.
                </p>
              )}
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-5">
          Trasferimento sicuro con Blagomail
        </p>
      </div>
    </div>
  );
}
